import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import AppSettingsSidebarIcon from "../icons/AppSettingsSidebarIcon";
import AdminOptionsIcon from "../icons/AdminOptionsIcon";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import CustomerSupportSidebarIcon from "../icons/CustomerSupportSidebarIcon";
import EmployeesIcon from "../icons/EmployeesIcon";
import LogsIcon from "../icons/LogsIcon";
import RegionSidebarIcon from "../icons/RegionSidebarIcon";
import SocialMediaSidebarIcon from "../icons/SocialMediaSidebarIcon";
import SquadsSidebarIcon from "../icons/SquadsSidebarIcon";
import TimelinesSidebarIcon from "../icons/TimelinesSidebarIcon";
import TournamentCommentsSidebarIcon from "../icons/TournamentCommentsSidebarIcon";
import TournamentSidebarIcon from "../icons/TournamentSidebarIcon";
import SupportTicketsSidebarIcon from "../icons/SupportTicketsSidebarIcon";
import UsersSidebarIcon from "../icons/UsersSidebarIcon";
import DeveloperIcon from "../icons/DeveloperIcon";
import PageLink from "../pageLink/PageLink";
import {
  hasAnyPermission,
  sectionOf,
  sectionPermissions,
} from "../../constants/permissionCatalog";
import Portal from "../portal/Portal";
import useSidebarCollapsed from "../../hooks/useSidebarCollapsed";
import styles from "./sideBar.module.css";

// Shown in this order, each when the role holds any of its permissions.
const ITEMS = [
  { permissions: sectionPermissions(sectionOf("admin-options")), icon: AdminOptionsIcon, destination: "/dashboard/admin-options", label: "خيارات الادمن" },
  { permissions: sectionPermissions(sectionOf("logs")), icon: LogsIcon, destination: "/dashboard/logs", label: "سجل العمليات" },
  { permissions: sectionPermissions(sectionOf("employees")), icon: EmployeesIcon, destination: "/dashboard/emlpoyees", label: "ادارة الموظفين" },
  { permissions: sectionPermissions(sectionOf("tournaments")), icon: TournamentSidebarIcon, destination: "/dashboard/tournaments", label: "ادارة البطولات" },
  { permissions: sectionPermissions(sectionOf("regions")), icon: RegionSidebarIcon, destination: "/dashboard/regions", label: "ادارة المناطق" },
  { permissions: sectionPermissions(sectionOf("users")), icon: UsersSidebarIcon, destination: "/dashboard/users", label: "ادارة اللاعبين" },
  { permissions: sectionPermissions(sectionOf("squads")), icon: SquadsSidebarIcon, destination: "/dashboard/squads", label: "ادارة السكوادات" },
  { permissions: sectionPermissions(sectionOf("timelines")), icon: TimelinesSidebarIcon, destination: "/dashboard/timelines", label: "الجداول الزمنية" },
  { permissions: sectionPermissions(sectionOf("customer-support")), icon: CustomerSupportSidebarIcon, destination: "/dashboard/customer-support", label: "شات المستخدمين" },
  { permissions: sectionPermissions(sectionOf("support-tickets")), icon: SupportTicketsSidebarIcon, destination: "/dashboard/support-tickets", label: "تذاكر الدعم" },
  { permissions: sectionPermissions(sectionOf("tournament-comments")), icon: TournamentCommentsSidebarIcon, destination: "/dashboard/tournaments-comments", label: "تعليقات البطولات" },
  { permissions: sectionPermissions(sectionOf("social-media")), icon: SocialMediaSidebarIcon, destination: "/dashboard/social-media", label: "السوشال ميديا" },
  { permissions: sectionPermissions(sectionOf("app-settings")), icon: AppSettingsSidebarIcon, destination: "/dashboard/app-settings", label: "التطبيق" },
  { permissions: sectionPermissions(sectionOf("developer")), icon: DeveloperIcon, destination: "/dashboard/developer", label: "أدوات المطور" },
];

const SideBar = () => {
  const { rolePermissions } = useSelector((state) => state.user);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  // Name label for the hovered/focused icon while collapsed. Rendered in a
  // portal at fixed coordinates so the scrolling list can't clip it.
  const [tooltip, setTooltip] = useState(null); // { label, top, right }

  const showTooltip = useCallback(
    (event, label) => {
      if (!collapsed) return;
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltip({
        label,
        top: rect.top + rect.height / 2,
        right: window.innerWidth - rect.left + 10,
      });
    },
    [collapsed],
  );
  const hideTooltip = useCallback(() => setTooltip(null), []);

  const toggle = () => {
    hideTooltip();
    toggleCollapsed();
  };

  return (
    <div className={`${styles.container} ${collapsed ? styles.collapsed : ""}`}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.toggle}
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "إظهار القائمة" : "إخفاء القائمة"}
          title={collapsed ? "إظهار القائمة" : "إخفاء القائمة"}
        >
          <ChevronDownIcon className={styles.toggleIcon} />
        </button>
      </div>
      <nav className={styles.list} onScroll={hideTooltip}>
        {ITEMS.filter((item) => hasAnyPermission(rolePermissions, item.permissions)).map((item) => (
          <div
            key={item.destination}
            className={styles.linkWrapper}
            onMouseEnter={(event) => showTooltip(event, item.label)}
            onMouseLeave={hideTooltip}
            onFocus={(event) => showTooltip(event, item.label)}
            onBlur={hideTooltip}
          >
            <PageLink
              icon={item.icon}
              destination={item.destination}
              iconOnly={collapsed}
              ariaLabel={collapsed ? item.label : undefined}
            >
              {item.label}
            </PageLink>
          </div>
        ))}
      </nav>
      {tooltip && (
        <Portal>
          <div
            role="tooltip"
            className={styles.tooltip}
            style={{ top: tooltip.top, right: tooltip.right }}
          >
            {tooltip.label}
          </div>
        </Portal>
      )}
    </div>
  );
};

export default SideBar;
